import {
  Injectable,
  Inject
} from '@angular/core';

import {
  TranslationService,
  TranslationConfig,
  LocaleService,
  TranslationProvider,
  TRANSLATION_CONFIG,
  TranslationHandler
} from 'angular-l10n';
import {
  Observable,
  Subject,
  BehaviorSubject,
  zip,
  from
} from 'rxjs';
import {
  map,
  switchMap, catchError
} from 'rxjs/operators';
import { unzip } from 'underscore';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Injectable()
export class LazyTranslationService extends TranslationService {
  private surveiled_translation: BehaviorSubject < string > = new BehaviorSubject < string > ('');
  constructor(
    @Inject(TRANSLATION_CONFIG) private _configuration: TranslationConfig,
    private _locale: LocaleService,
    private _translationProvider: TranslationProvider,
    private _translationHandler: TranslationHandler,
    private http: HttpClient
  ) {
    super(_configuration, _locale, _translationProvider, _translationHandler);
    this.translationChanged().subscribe(this.surveiled_translation);

  }
  public translateAsync(
    keys: string | string[],
    args ?: any,
    lang: string = this.surveiled_translation.getValue()
  ): Observable < string | any > {
    return super.translateAsync(keys, args, lang).pipe(map(translations => {
        if (!this.getConfiguration().providers.find(v => v.type === 'incremental')) {
          return from([translations]);
        }
        // split the language code
        const baseLang = lang.split('-')[0];
        let retryKeys = [];
        if (Array.isArray(keys)) {
          retryKeys = translations.filter(k => k === 'No key').map(v => v.replace('&', '%26'));
          // retry with the API
        } else {
          if (translations === 'No key') {
            retryKeys = [keys].map(v => v.replace('&', '%26'));
          }
        }
        if (retryKeys.length === 0) {
          return from([translations]);
        }
        // run all simultaneously
        const asyncTranslations = zip(...this.getConfiguration()
          .providers.filter(v => v.type === 'incremental')
          .map(provider => {
            const options = {
              headers: new HttpHeaders({}),
            };
            if (provider.incrementalFallbackLanguage) {
              return this.http.get(`${provider.prefix}/${baseLang}?keys=${JSON.stringify(retryKeys)}`, options).pipe(catchError(error => []))
            }
            return this.http.get(`${provider.prefix}/${lang}?keys=${JSON.stringify(retryKeys)}`, options).pipe(catchError(error => []))
          })).pipe(map(translationSet => {
            // time to decide which translation to use
            // reduce leftwards (stopping at the first successful key)
            // merge successful and unsuccessful translations for each stream, in the original order of keys
            if (Array.isArray(keys)) {
                return unzip(translationSet.map((v: any) => {
                    const successfulKeys = Object.keys(v.translations);
                    return keys.map((k, idx) => {
                      // if the key is in the successfulKeys, grab its value, return immediately
                      if (successfulKeys.indexOf(k) !== -1) {
                        return v.translations[k];
                      } else if (v.unsuccessful.indexOf(k) !== -1) {
                        return `No key`;
                      }
                      return translations[idx];
                    });
                  })).map(v =>
                  v.reduceRight(
                    (acc, curr) =>
                      acc === 'No key' ? curr : acc,
                    `~${keys}`
                  )
                );
            } else {
                return translationSet.map((v: any) => {
                    return v.translations[keys] ? v.translations[keys] : `~${keys}`;
                }).reduceRight((acc, curr) => acc === `~${keys}` ? curr : acc, `~${keys}`);
            }
        }));
        return asyncTranslations;
      }), switchMap(v => {
          return v;
      }));
  }
}
